/**
 * Shared types and helpers for the GA4 traffic scene.
 * Used by index.tsx (single-portal view) and PortalComparisonView.tsx (compare mode).
 */

import moment from 'moment';

// ─── Types ─────────────────────────────────────────────────────────────────

export type PortalGroup = 'finanztrends' | 'portale_robert' | 'non_finanz' | 'auslaendisch';

export interface Portal {
  key: string;
  name: string;
  group: PortalGroup;
  primary: boolean;
  hasLeads: boolean;
}

export interface Overview {
  totalUsers: number;
  newUsers: number;
  sessions: number;
  pageViews: number;
  avgSessionDurationSeconds: number;
  engagementRate: number;
}

export interface CompareRange {
  startDate: string;
  endDate: string;
}

export interface OverviewCompareResponse {
  current: Overview;
  previous: Overview;
  currentRange: CompareRange;
  previousRange: CompareRange;
}

export interface TimeseriesRow extends Overview {
  date: string;
}

export interface LeadsCompareResponse {
  current: { organicLeads: number };
  previous: { organicLeads: number };
  organicTimeseries: { date: string; leads: number }[];
}

export type MetricKey =
  | 'totalUsers'
  | 'newUsers'
  | 'sessions'
  | 'pageViews'
  | 'engagementRate'
  | 'avgSessionDurationSeconds'
  | 'leads'
  | 'leadsPerKUsers';

export const METRIC_LABELS: Record<MetricKey, string> = {
  totalUsers: 'Unique Users',
  newUsers: 'Neue Users',
  sessions: 'Sitzungen',
  pageViews: 'Seitenaufrufe',
  engagementRate: 'Engagement-Rate',
  avgSessionDurationSeconds: 'Ø Sitzungsdauer',
  leads: 'Gesamt-Leads (Traffic)',
  leadsPerKUsers: 'Leads / 1.000 Users',
};

/**
 * Tooltip texts shown next to each metric label (KPI cards + table headers).
 * German, deliberately precise — the lead metrics in particular need to
 * explain why the number can differ from daily controlling.
 */
export const METRIC_TOOLTIPS: Record<MetricKey, string> = {
  totalUsers:
    'Anzahl eindeutiger Besucher im gewählten Zeitraum. GA4 dedupliziert wiederkehrende Besucher serverseitig pro Bucket.',
  newUsers: 'Erstbesucher, die in diesem Zeitraum zum allerersten Mal auf der Website waren.',
  sessions:
    'Anzahl aller Besuche (Sitzungen). Eine Sitzung endet nach 30 Min Inaktivität oder bei Mitternacht.',
  pageViews:
    'Gesamtzahl angesehener Seiten — inklusive mehrfacher Aufrufe der gleichen Seite innerhalb einer Sitzung.',
  engagementRate:
    'Anteil der Sitzungen, die mindestens 10 Sekunden dauerten, mehr als eine Seite hatten oder einen Conversion-Event auslösten.',
  avgSessionDurationSeconds:
    'Durchschnittliche Dauer einer Sitzung in Minuten:Sekunden, gewichtet nach Sitzungsanzahl pro Tag.',
  leads:
    "Alle DOI-Formularabschlüsse aus organischem Traffic (Quellen: traffic, ai_articles, other_articles). Inkludiert neue Leads, Wiederanmeldungen (Duplikate) und Tag-Updates. Diese Zahl ist daher in der Regel höher als 'neue Leads' im Daily Controlling, da dort nur tatsächliche Erstkontakte gezählt werden.",
  leadsPerKUsers:
    'Conversion-Rate: Anzahl organischer Lead-Submissions pro 1.000 eindeutiger Besucher. Höhere Werte bedeuten bessere Trichter-Performance.',
};

export const LEAD_METRICS: MetricKey[] = ['leads', 'leadsPerKUsers'];

// ─── Groups ────────────────────────────────────────────────────────────────

export const GROUP_ORDER: PortalGroup[] = [
  'finanztrends',
  'portale_robert',
  'non_finanz',
  'auslaendisch',
];

export const GROUP_SHORT_LABELS: Record<PortalGroup, string> = {
  finanztrends: 'FT',
  portale_robert: 'Robert',
  non_finanz: 'Non-Finanz',
  auslaendisch: 'Ausland',
};

// ─── Formatters ────────────────────────────────────────────────────────────

export const fmtInt = (n: number) => Math.round(n || 0).toLocaleString('de-DE');

export const fmtPercent = (n: number) => `${((n || 0) * 100).toFixed(1)} %`;

export const fmtDuration = (seconds: number) => {
  const s = Math.round(seconds || 0);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${mm}:${ss.toString().padStart(2, '0')}`;
};

export const fmtRate = (n: number) => n.toFixed(1);

export const toSqlDate = (d: Date) => moment(d).format('YYYY-MM-DD');

/**
 * Percent change from previous→current. Returns null if previous is 0 (no base).
 */
export function calcDelta(current: number, previous: number): number | null {
  if (!previous) return null;
  return ((current - previous) / previous) * 100;
}

/**
 * Format a metric value for display (table cell, card, tooltip).
 */
export function formatMetricValue(metric: MetricKey, value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  switch (metric) {
    case 'engagementRate':
      return fmtPercent(value);
    case 'avgSessionDurationSeconds':
      return fmtDuration(value);
    case 'leadsPerKUsers':
      return fmtRate(value);
    default:
      return fmtInt(value);
  }
}

// ─── Quick date ranges ─────────────────────────────────────────────────────

export interface QuickRange {
  key: string;
  label: string;
  compute: () => { start: Date; end: Date };
}

export const QUICK_RANGES: QuickRange[] = [
  {
    key: '7d',
    label: '7 Tage',
    compute: () => ({
      start: moment().subtract(7, 'days').toDate(),
      end: moment().subtract(1, 'day').toDate(),
    }),
  },
  {
    key: '14d',
    label: '14 Tage',
    compute: () => ({
      start: moment().subtract(14, 'days').toDate(),
      end: moment().subtract(1, 'day').toDate(),
    }),
  },
  {
    key: '30d',
    label: '30 Tage',
    compute: () => ({
      start: moment().subtract(30, 'days').toDate(),
      end: moment().subtract(1, 'day').toDate(),
    }),
  },
  {
    key: '3m',
    label: '3 Monate',
    compute: () => ({
      start: moment().subtract(3, 'months').toDate(),
      end: moment().subtract(1, 'day').toDate(),
    }),
  },
  {
    key: '6m',
    label: '6 Monate',
    compute: () => ({
      start: moment().subtract(6, 'months').toDate(),
      end: moment().subtract(1, 'day').toDate(),
    }),
  },
  {
    key: 'ytd',
    label: 'YTD',
    compute: () => ({
      start: moment().startOf('year').toDate(),
      end: moment().subtract(1, 'day').toDate(),
    }),
  },
  {
    key: '12m',
    label: '12 Monate',
    compute: () => ({
      start: moment().subtract(12, 'months').toDate(),
      end: moment().subtract(1, 'day').toDate(),
    }),
  },
];

// ─── Compare entry shape (per-portal data in compare mode) ────────────────

export interface CompareEntry {
  portal: Portal;
  overview: Overview | null;
  previousOverview: Overview | null;
  previousRange: CompareRange | null;
  timeseries: TimeseriesRow[];
  organicLeads: number | null;
  previousOrganicLeads: number | null;
  organicTimeseries: { date: string; leads: number }[];
  error?: string;
}

/**
 * Extract a metric's numeric value from a CompareEntry.
 * Returns null for metrics that don't exist for this portal (e.g. leads on non-lead portals).
 */
export function extractCompareMetric(entry: CompareEntry, metric: MetricKey): number | null {
  if (!entry.overview) return null;

  switch (metric) {
    case 'totalUsers':
    case 'newUsers':
    case 'sessions':
    case 'pageViews':
    case 'avgSessionDurationSeconds':
    case 'engagementRate':
      return entry.overview[metric];
    case 'leads':
      return entry.organicLeads;
    case 'leadsPerKUsers':
      if (
        entry.organicLeads === null ||
        !entry.overview.totalUsers ||
        entry.overview.totalUsers === 0
      ) {
        return null;
      }
      return (entry.organicLeads / entry.overview.totalUsers) * 1000;
    default:
      return null;
  }
}

/**
 * Extract previous-period value for a metric (for delta computation in compare mode).
 */
export function extractPreviousMetric(entry: CompareEntry, metric: MetricKey): number | null {
  if (!entry.previousOverview) return null;

  switch (metric) {
    case 'totalUsers':
    case 'newUsers':
    case 'sessions':
    case 'pageViews':
    case 'avgSessionDurationSeconds':
    case 'engagementRate':
      return entry.previousOverview[metric];
    case 'leads':
      return entry.previousOrganicLeads;
    case 'leadsPerKUsers':
      if (
        entry.previousOrganicLeads === null ||
        !entry.previousOverview.totalUsers ||
        entry.previousOverview.totalUsers === 0
      ) {
        return null;
      }
      return (entry.previousOrganicLeads / entry.previousOverview.totalUsers) * 1000;
    default:
      return null;
  }
}

// ─── Time-series aggregation ───────────────────────────────────────────────
// Server-side bucketing is the source of truth: GA4 returns one row per
// day/week/month with correctly-deduped uniques and weighted rates, and the
// /api/ga4/leads SQL groups via COUNT(DISTINCT process_id). The frontend
// never aggregates client-side anymore — it just plots the rows it gets.
//
// Helpers below exist for chart axis formatting only (they parse the date
// string returned by the backend into a German label).

export type AggregationMode = 'auto' | 'daily' | 'weekly' | 'monthly';

/**
 * Map the frontend's three-mode aggregation label to the backend's bucket
 * query parameter. Used in fetch URL construction.
 */
export function aggregationToBucket(
  aggregation: 'daily' | 'weekly' | 'monthly',
): 'day' | 'week' | 'month' {
  if (aggregation === 'weekly') return 'week';
  if (aggregation === 'monthly') return 'month';
  return 'day';
}

/**
 * ISO week number for a date (Monday = day 1 of the week).
 * Copied from LeadSourceChart.tsx — canonical implementation for this codebase.
 * Used by formatAggregationTick to render "KW X, Mmm" labels on the chart axis.
 */
export function getISOWeek(date: Date): number {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

/**
 * Format a date/period key for axis ticks + tooltips.
 * Matches the pattern in chart-patterns.md:
 *  - daily   → "DD.MM"
 *  - weekly  → "KW X, Mmm"
 *  - monthly → "Mmm YYYY"
 *
 * Inputs are ISO date strings as returned by the backend:
 *  - daily   → "2026-04-15"
 *  - weekly  → "2026-04-13" (ISO Monday of that week)
 *  - monthly → "2026-04-01"
 */
export function formatAggregationTick(
  key: string,
  aggregation: 'daily' | 'weekly' | 'monthly',
): string {
  const date = new Date(key);
  if (Number.isNaN(date.getTime())) return key;

  switch (aggregation) {
    case 'monthly':
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'short',
      });
    case 'weekly': {
      const weekNum = getISOWeek(date);
      const month = date.toLocaleDateString('de-DE', { month: 'short' });
      return `KW ${weekNum}, ${month}`;
    }
    case 'daily':
    default:
      return date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
      });
  }
}
